import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Globe, CreditCard, Hammer, HardDrive, ExternalLink, Info, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import type { LucideIcon } from "lucide-react";
import { formatTimestamp } from "@pgs-core/portal-lib/format";
import { billingStatusBadgeVariant, billingStatusLabel } from "@pgs-core/portal-lib/billing";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { deriveBuildState } from "../lib/build";
import type { ClientOutletContext } from "../lib/outlet-context";

function StatCard({ title, icon: Icon, children }: {
  title: React.ReactNode;
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}


function formatStorage(usedGb: number | null | undefined, quotaGb: number | null | undefined) {
  if (typeof usedGb !== "number") return "—";
  const used = usedGb < 0.01 ? "<0.01" : usedGb.toFixed(2);
  return quotaGb ? `${used} / ${quotaGb} GB` : `${used} GB`;
}

export function DashboardPage() {
  const { status, statusError } = useOutletContext<ClientOutletContext>();

  const siteUrl = status?.site_url ?? "";
  const build = deriveBuildState(status);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="A snapshot of your website, billing, and recent publishes."
      />

      {statusError && (
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Status unavailable</AlertTitle>
          <AlertDescription>
            We couldn't load your site status right now ({statusError}). The numbers
            below will fill in once it's back.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Your website" icon={Globe}>
          {siteUrl ? (
            <a
              href={siteUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {siteUrl.replace(/^https?:\/\//, "")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <div className="text-2xl font-bold">—</div>
          )}
          <p className="mt-1 text-xs text-muted-foreground">Live site address</p>
        </StatCard>

        <StatCard title="Billing" icon={CreditCard}>
          <Link to="/billing" className="inline-block" title="View billing">
            <Badge variant={billingStatusBadgeVariant(status?.billing_status)}>
              {billingStatusLabel(status?.billing_status)}
            </Badge>
          </Link>
          <p className="mt-2 text-xs text-muted-foreground">
            Next payment: {formatTimestamp(status?.next_payment_date)}
          </p>
          <Link to="/billing" className="mt-1 inline-block text-xs font-medium text-primary hover:underline">
            View plan &amp; invoices
          </Link>
        </StatCard>

        <StatCard title="Site build" icon={Hammer}>
          <Badge variant={build.badgeVariant} className="gap-1">
            {build.inProgress && <Loader2 className="h-3 w-3 animate-spin" />}
            {build.label}
          </Badge>
          <p className="mt-2 text-xs text-muted-foreground">
            {build.inProgress
              ? "Your changes go live in about 2 minutes."
              : build.at
                ? formatTimestamp(build.at)
                : "Publish a change to build your site."}
          </p>
        </StatCard>

        <StatCard title="Storage" icon={HardDrive}>
          <div className="text-2xl font-bold">
            {formatStorage(status?.storage_used_gb, status?.storage_quota_gb)}
          </div>
          <p className="text-xs text-muted-foreground">Media and content used</p>
        </StatCard>
      </div>
    </div>
  );
}
